import React, { Fragment, FunctionComponent, HTMLAttributes, memo, ReactElement, useCallback, useMemo, useRef, useState } from "react";

import { bemClassNames } from "@multiweb/reactUtils";
import { DEBOUNCE_TIME_DELAY, DRAG_TRANSFER_KEY } from "./constants";
import { DraggableItemProvider, DraggableItemProviderComponentChildrenProps } from "./DraggableItemProvider";
import { DragOverPosition, ItemRendererType } from "./types";
import { createDataTransferKey, getDragOverPosition, getItemIdFromKey } from "./utils";

export type DraggableTreeItemProp = {
    /** ID prvku stromu. */
    readonly itemId: string;
    /** Příznak pro ignorování středové pozice. */
    readonly ignoreMiddlePosition: boolean;
    /** Funkce vracející podobu prvku stromu. */
    readonly itemRenderer: ItemRendererType;
    /** Callback volaný při dropnutí prvku stromu. */
    readonly changeItemsHierarchy: (itemId: string, toItemId: string, hierarchyPosition: DragOverPosition) => void;
    /** Callback volaný při dragOver, který určuje, jestli může prvek být dropnut. */
    readonly canDropItem: (draggedItemId: string, toItemId: string, hierarchyPosition: DragOverPosition) => boolean;
};

const classNames = bemClassNames("draggable-tree-item");

export const DraggableTreeItem: FunctionComponent<DraggableTreeItemProp> = memo(({ itemId, ignoreMiddlePosition, itemRenderer, changeItemsHierarchy, canDropItem }) => {
    const currentDropEffectRef = useRef<"link" | "none" | "copy" | "move">("move");
    const isDebounced = useRef(false);
    const [dragOverPosition, setDragOverPosition] = useState<DragOverPosition | null>(null);

    const htmlElementProps: HTMLAttributes<HTMLElement> = useMemo(() => ({
        draggable: true,
        onDragStart: (e) => e.dataTransfer.setData(createDataTransferKey(DRAG_TRANSFER_KEY, itemId), itemId),
        onDragEnd: () => setDragOverPosition(null),
        onDragEnter: () => {
            isDebounced.current = false;
            setTimeout(() => isDebounced.current = true, DEBOUNCE_TIME_DELAY);
        },
        onDragLeave: (e) => {
            if (e.currentTarget === e.target)
                setDragOverPosition(null);
        },
        onDragOver: (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = currentDropEffectRef.current;

            if (!isDebounced.current)
                return;

            const newDragOverPosition = getDragOverPosition(e, ignoreMiddlePosition);

            if (newDragOverPosition === dragOverPosition) {
                return;
            }

            setDragOverPosition(newDragOverPosition);
            const draggedItemId = getItemIdFromKey(e.dataTransfer.types[0]);

            if (!canDropItem(draggedItemId, itemId, newDragOverPosition)) {
                e.dataTransfer.dropEffect = currentDropEffectRef.current = "none";
                return;
            }

            currentDropEffectRef.current = e.dataTransfer.dropEffect = "copy";
        },
        onDrop: (e) => {
            e.preventDefault();

            if (dragOverPosition)
                changeItemsHierarchy(e.dataTransfer.getData(e.dataTransfer.types[0]), itemId, dragOverPosition);

            setDragOverPosition(null);
        },
    }), [dragOverPosition, itemId, ignoreMiddlePosition, changeItemsHierarchy, canDropItem]);

    const innerRenderer = useCallback<(props: DraggableItemProviderComponentChildrenProps) => ReactElement>(({ item }) =>
        <Fragment>
            {itemRenderer(htmlElementProps, item, dragOverPosition)}
            {(item.expanded && item.children.length !== 0) && (
                <div className={classNames()}>
                    {item.children.map(childId =>
                        <DraggableTreeItem key={childId} itemId={childId} {...{ itemRenderer, changeItemsHierarchy, ignoreMiddlePosition, canDropItem }} />,
                    )}
                </div>
            )}
        </Fragment>, [htmlElementProps, dragOverPosition, ignoreMiddlePosition, itemRenderer, changeItemsHierarchy, canDropItem]);

    return (
        <DraggableItemProvider itemId={itemId}>
            {innerRenderer}
        </DraggableItemProvider>
    );
});
